import type { Location } from '@/components/azure-map/azureMapsSdk'
import { ALL_OPTION, getProvinceOptions, resolveProvince, withAllOption } from '@/routes/map/SitesMapPage.provinces'
import type {
  MapBounds,
  MapSite,
  SiteMapFilterOptions,
  SiteMapFilters,
  SiteMapPoint,
} from '@/routes/map/SitesMapPage.types'

const EARTH_RADIUS_KM = 6371
const KM_PER_LATITUDE_DEGREE = 111

export const DEFAULT_FILTERS: SiteMapFilters = {
  region: ALL_OPTION,
  province: ALL_OPTION,
  city: ALL_OPTION,
  relationshipType: ALL_OPTION,
  agent: ALL_OPTION,
  onlyMainSites: false,
}

export const toMapSite = (point: SiteMapPoint): MapSite => ({ ...point, ...resolveProvince(point.province) })

const normalize = (value: string | null | undefined) => value?.trim().toUpperCase() ?? ''

const matchesOption = (option: string, value: string | null) =>
  option === ALL_OPTION || normalize(option) === normalize(value)

export const hasActiveFilters = (filters: SiteMapFilters, search: string) =>
  Boolean(search) ||
  filters.onlyMainSites ||
  [filters.region, filters.province, filters.city, filters.relationshipType, filters.agent].some(
    (option) => option !== ALL_OPTION,
  )

export const filterSites = (sites: MapSite[], filters: SiteMapFilters, search: string) => {
  const term = search.toLowerCase()
  return sites.filter(
    (site) =>
      matchesOption(filters.region, site.region) &&
      matchesOption(filters.province, site.provinceName) &&
      matchesOption(filters.city, site.city) &&
      matchesOption(filters.relationshipType, site.relationshipType) &&
      matchesOption(filters.agent, site.agentName) &&
      (!filters.onlyMainSites || site.isMainSite) &&
      (!term || [site.companyName, site.siteName, site.city].some((value) => value?.toLowerCase().includes(term))),
  )
}

/** Options come from every geocoded site, so they do not depend on the visible area or on the other filters. */
export const getFilterOptions = (sites: MapSite[], filters: SiteMapFilters): SiteMapFilterOptions => {
  const sitesInArea = sites.filter(
    (site) => matchesOption(filters.region, site.region) && matchesOption(filters.province, site.provinceName),
  )
  const present = (values: (string | null)[]) => values.filter((value): value is string => Boolean(value?.trim()))
  return {
    provinces: getProvinceOptions(filters.region),
    cities: withAllOption(present(sitesInArea.map((site) => site.city))),
    relationshipTypes: withAllOption(present(sites.map((site) => site.relationshipType)).map(normalize)),
    agents: withAllOption(present(sites.map((site) => site.agentName))),
  }
}

const toRadians = (degrees: number) => (degrees * Math.PI) / 180

const getDistanceKm = (from: Location, to: Location) => {
  const deltaLat = toRadians(to.lat - from.lat)
  const deltaLon = toRadians(to.lon - from.lon)
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(deltaLon / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export const sortSites = (sites: MapSite[], userLocation: Location | null): MapSite[] => {
  if (!userLocation) return [...sites].sort((a, b) => a.companyName.localeCompare(b.companyName))
  return sites
    .map((site) => ({ ...site, distanceKm: getDistanceKm(userLocation, { lat: site.latitude, lon: site.longitude }) }))
    .sort((a, b) => a.distanceKm - b.distanceKm)
}

export const getBoundingBox = ({ lat, lon }: Location, radiusKm: number): MapBounds => {
  const latDelta = radiusKm / KM_PER_LATITUDE_DEGREE
  const lonDelta = radiusKm / (KM_PER_LATITUDE_DEGREE * Math.cos(toRadians(lat)))
  return [lon - lonDelta, lat - latDelta, lon + lonDelta, lat + latDelta]
}

const clamp = (value: number, limit: number) => Math.round(Math.min(Math.max(value, -limit), limit) * 1e4) / 1e4

/** Clamps and rounds camera bounds used for viewport filtering in the browser. */
export const toQueryBounds = ([minLon, minLat, maxLon, maxLat]: number[]): MapBounds => [
  clamp(minLon, 180),
  clamp(minLat, 90),
  clamp(maxLon, 180),
  clamp(maxLat, 90),
]

/** Radius shown next to the list when the map is centred on the user, derived from the zoom level. */
export const getRadiusKm = (zoom: number) => {
  if (zoom >= 12.5) return 5
  if (zoom >= 11) return 10
  if (zoom >= 10) return 20
  if (zoom >= 9) return 40
  if (zoom >= 7) return 100
  return null
}
